/**
 * Frontend step definitions per flow.
 *
 * Step IDs are the contract with the server (FLOW_STEP_IDS). Don't
 * rename without a migration. Order here defines the user-visible
 * progression — the engine picks the first not-completed step.
 */
import type { FlowKey } from '@builder/shared';
import type { StepDef } from './types';

export const PM_STEPS: StepDef[] = [
  {
    id: 'welcome', kind: 'slideout',
    title: 'Bem-vindo ao Builder 👋',
    body:
      'Builder é o sistema de gestão da sua obra — do orçamento ao acabamento. ' +
      'Vamos fazer um tour rápido de 3 minutos para você dominar as partes que mais importam.',
    ctaLabel: 'Começar tour',
  },
  {
    id: 'open_project', kind: 'tooltip', route: '/projects',
    targetAttr: 'project-card', placement: 'bottom',
    title: 'Seus projetos',
    body: 'Cada card representa uma obra. Clique para abrir e ver o painel completo.',
  },
  {
    id: 'stage_tracker', kind: 'tooltip',
    targetAttr: 'stage-tracker-tab', placement: 'bottom',
    title: 'Rastreador de etapas',
    body:
      'Veja e atualize o avanço físico da obra por etapa. ' +
      'O percentual aqui é o que alimenta o relatório do dono.',
  },
  {
    id: 'daily_log', kind: 'tooltip',
    targetAttr: 'daily-log-tab', placement: 'bottom',
    title: 'Diário de obra',
    body: 'Registre diariamente: equipe, atividades, clima e ocorrências. É a memória oficial da obra.',
  },
  {
    id: 'photos_tab', kind: 'tooltip',
    targetAttr: 'photos-tab', placement: 'bottom',
    title: 'Fotos e álbuns',
    body: 'Documente o avanço com fotos organizadas em álbuns. Donos veem sem acesso ao financeiro.',
  },
  {
    id: 'budget_tab', kind: 'tooltip',
    targetAttr: 'budget-kpi-cards', placement: 'bottom',
    title: 'Planejado × Real',
    body:
      'Os três números que importam: orçado, comprometido (OCs abertas) e realizado (pagamentos). ' +
      'O desvio é calculado automaticamente.',
  },
  {
    id: 'purchase_order', kind: 'tooltip',
    targetAttr: 'new-po-button', placement: 'left',
    title: 'Ordens de compra',
    body:
      'Crie uma OC para qualquer material ou serviço. ' +
      'Após aprovada, vira automaticamente um comprometimento no orçamento.',
  },
  {
    id: 'agent_chat', kind: 'slideout',
    title: 'Conheça o Housy — sua IA de obra',
    body:
      'Housy é um agente de IA treinado nos dados do seu projeto. ' +
      'Pergunte em linguagem natural: "Qual o desvio do orçamento?" ou "Mostre as fotos da semana." ' +
      'Ele acessa tudo em tempo real.',
    ctaLabel: 'Abrir o Housy',
  },
  {
    // Wave 100 (#258) — call out the post-Wave-95 surfaces so PMs
    // who took the tour months ago discover the new modules without
    // having to read the changelog. Slideout (no DOM target) keeps
    // it from breaking when any of the modules' UI changes.
    id: 'new_modules', kind: 'slideout',
    title: 'Novidades para você explorar',
    body:
      'Além do básico, o Builder agora tem: BDI por projeto · Verba mensal · Composição de insumos · ' +
      'Apropriações · Demonstrativo gerencial · Cotação comparativa · Mão-de-obra (ponto) · Estoque · ' +
      'Checklists customizados · Photo albums · Relatórios montados em blocos · Anomalias (guardrails) · ' +
      'verificação automática de CNPJ/CEIS. Cada um está dentro da obra ou no menu superior. ' +
      'Pra um índice completo, abra Ajuda → "Módulos avançados".',
    ctaLabel: 'Entendi',
  },
  {
    // #266 wave — call out the April 2026 surfaces. Slideout (no DOM
    // target) so it stays robust if any of the named UIs change later.
    id: 'apr2026_updates', kind: 'slideout',
    title: 'Novidades de abril 2026',
    body:
      'Quatro coisas que mudaram desde a última vez:\n' +
      '• Painel de usuários (em /admin) tem agora "Detalhes" com últimas atividades, ' +
      'sessões ativas, projetos atribuídos e histórico — para admins e gerentes.\n' +
      '• Gerentes (PM) podem gerenciar trabalhadores/mestres/clientes/corretores ' +
      'pelo mesmo painel, sem mexer em outros admins ou PMs.\n' +
      '• Apagar uma obra agora é um clique — botão lixeira no card, com confirmação ' +
      'por digitar o nome.\n' +
      '• Portal do Cliente ganhou "✨ Gerar com IA" para escrever headline e SEO a partir do projeto.',
    ctaLabel: 'Entendi',
  },
  {
    // Wave 301 — late-April polish sweep. Slideout (no DOM target) so
    // it survives even if the surfaces below get refactored. The user
    // doesn't need to *do* anything for these — they're behavioral
    // changes that just become visible on the next interaction.
    id: 'apr2026_polish', kind: 'slideout',
    title: 'Polimento e segurança (fim de abril 2026)',
    body:
      'Quatro coisas que ficaram melhores nos últimos dias:\n' +
      '• Acessibilidade: cursor laranja visível no teclado em qualquer ' +
      'tela; cabeçalhos não quebram em órfãos; números nas KPIs param ' +
      'de balançar entre atualizações.\n' +
      '• Segurança: a sessão expira sozinha quando ociosa, mensagens ' +
      'longas para a Housy ficam limitadas, e tentativas suspeitas de ' +
      'injeção de prompt são bloqueadas em silêncio.\n' +
      '• Portal do Cliente (genese.nullscience.ai): foco visível, ' +
      'imagens em três tamanhos (carrega o menor que cabe) e a ordem ' +
      'agora é "Em linha" antes de "Em matéria".\n' +
      '• Caixa de email do Builder: cada projeto/usuário pode ter um ' +
      'endereço @nullscience.ai que entra direto na inbox — pergunte ' +
      'à Housy "qual meu email do Builder?" para ver o seu.',
    ctaLabel: 'Entendi',
  },
  {
    // Wave 303 — inbound email is live. Discoverability hinge: most
    // users won't try this without prompting. Slideout (no DOM target)
    // because the surface lives in /configuracoes#email-inbox.
    id: 'inbox_email_303', kind: 'slideout',
    title: 'Builder agora recebe email',
    body:
      'Você tem um endereço @nullscience.ai que vira mensagem aqui dentro do Builder.\n\n' +
      '• Pergunte à Housy "qual meu email no Builder?" pra ver/criar o seu.\n' +
      '• "Muda meu email pra jeferson" cria um apelido amigável (jeferson@nullscience.ai). ' +
      'O endereço-hash original continua funcionando.\n' +
      '• "Bloqueia spam@x.com" elimina aquele remetente antes dele chegar na inbox. ' +
      'Domínio inteiro também: "bloqueia evil.com".\n' +
      '• Em Configurações → Email, você vê todos os endereços, contadores dos últimos 7 dias ' +
      'e a lista de bloqueados.',
    ctaLabel: 'Entendi',
  },
  {
    // Wave 312-314 — Prumo, the operational copilot. Slideout (no DOM
    // target) because the badge sits in TopBar and a tooltip there
    // competes with Housy. The body explains the role split.
    id: 'prumo_312', kind: 'slideout',
    title: 'Conheça o Prumo — copiloto operacional',
    body:
      'O Prumo é irmão do Housy: mesma inteligência, surface diferente.\n\n' +
      '• Topo da tela: clica no badge "Prumo" (ou Cmd-Shift-A) para abrir a fila ' +
      'de cartões pré-decididos. Cada cartão é uma ação que ele já preparou pra você ' +
      '— aprovar uma PO, rascunhar o RDO de hoje, calcular a medição do mês — com ' +
      'fontes, riscos e alternativas. Você só toca em "Aprovar" ou "Recusar".\n\n' +
      '• Páginas internas: nas telas de Compras, RDO e Medições aparece um banner ' +
      '"Prumo · X ações nesta área" quando há cartões relevantes.\n\n' +
      '• Regras de auto-aprovação (Configurações → Autonomia, admin/PM): ex. ' +
      '"PO até R$ 5k de fornecedor top-3 sem sanção" → aprova sozinho. Tier 3 ' +
      '(alta blast-radius) NUNCA roda sozinho, regra ou não.\n\n' +
      'Pergunta ao Housy "tem coisa pra eu aprovar?" e ele te aponta pro Prumo.',
    ctaLabel: 'Entendi',
  },
  {
    id: 'complete', kind: 'slideout',
    title: '🎉 Tour concluído!',
    body:
      'Você já conhece os principais recursos. ' +
      'Para rever, abra o menu de conta → "Refazer tour". O Housy também responde dúvidas.',
    ctaLabel: 'Fechar',
  },
];

export const FINANCEIRO_STEPS: StepDef[] = [
  {
    id: 'welcome_fin', kind: 'slideout',
    title: 'Builder Financeiro 💼',
    body:
      'Você tem acesso completo ao módulo financeiro. ' +
      'Em 3 minutos vamos mostrar como o Builder substitui suas planilhas de controle de obra.',
    ctaLabel: 'Começar tour',
  },
  {
    id: 'projects_overview', kind: 'tooltip', route: '/projects',
    targetAttr: 'projects-list', placement: 'bottom',
    title: 'Carteira de obras',
    body: 'Cada card mostra o desvio orçamentário consolidado — verde dentro do budget, vermelho estourado.',
  },
  {
    id: 'budget_overview', kind: 'tooltip',
    targetAttr: 'budget-kpi-cards', placement: 'bottom',
    title: 'KPIs financeiros em tempo real',
    body:
      'Planejado · Comprometido · Realizado. ' +
      'Comprometido = OCs aprovadas ainda não pagas. Realizado = pagamentos efetivos.',
  },
  {
    id: 'bdi_panel', kind: 'tooltip',
    targetAttr: 'bdi-config-panel', placement: 'right',
    title: 'BDI por projeto',
    body:
      'Configure o BDI (Benefícios e Despesas Indiretas) em pontos-base. 1% = 100 bp. ' +
      'O sistema recalcula o orçamento automaticamente.',
  },
  {
    id: 'verba_mensal', kind: 'tooltip',
    targetAttr: 'verba-page-header', placement: 'bottom',
    title: 'Verba mensal',
    body:
      'Defina a verba planejada mês a mês e compare com o realizado. ' +
      'Ideal para fluxo de caixa e relatórios de diretoria.',
  },
  {
    id: 'purchase_orders_fin', kind: 'tooltip',
    targetAttr: 'po-status-filter', placement: 'bottom',
    title: 'Gestão de OCs',
    body: 'Filtre por status: pendente · aprovada · recebida · paga. Aprovar move o valor para "comprometido".',
  },
  {
    id: 'demonstrativo', kind: 'tooltip',
    targetAttr: 'demonstrativo-tab', placement: 'top',
    title: 'Demonstrativo gerencial',
    body: 'Apropriações por etapa ou categoria, com export para PDF/Excel direto desta tela.',
  },
  {
    id: 'relatorios', kind: 'tooltip',
    targetAttr: 'relatorios-page-header', placement: 'bottom',
    title: 'Relatórios customizáveis',
    body: 'Monte relatórios com os blocos que quiser. Salve templates para reutilizar toda semana.',
  },
  {
    id: 'complete_fin', kind: 'slideout',
    title: '🎉 Configuração completa!',
    body:
      'Você já sabe onde estão os números. ' +
      'Abra um projeto real e confira se o orçamento está importado. Housy responde dúvidas em linguagem natural.',
    ctaLabel: 'Fechar',
  },
];

export const FOREMAN_STEPS: StepDef[] = [
  {
    id: 'welcome_foreman', kind: 'slideout',
    title: 'Builder para Mestres de Obra 🏗️',
    body:
      'Seu foco é no canteiro. Em 2 minutos você vê onde registrar diário, atualizar etapas e tirar fotos.',
    ctaLabel: 'Começar',
  },
  {
    id: 'open_project_foreman', kind: 'tooltip', route: '/projects',
    targetAttr: 'project-card', placement: 'bottom',
    title: 'Sua obra', body: 'Clique no card da sua obra para entrar.',
  },
  {
    id: 'stage_tracker_foreman', kind: 'tooltip',
    targetAttr: 'stage-tracker-tab', placement: 'bottom',
    title: 'Etapas', body: 'Atualize o percentual de cada etapa conforme o avanço real no canteiro.',
  },
  {
    id: 'daily_log_foreman', kind: 'tooltip',
    targetAttr: 'daily-log-tab', placement: 'bottom',
    title: 'Diário de obra', body: 'Registre diariamente: quem trabalhou, o que foi feito, qualquer ocorrência.',
  },
  {
    id: 'photos_foreman', kind: 'tooltip',
    targetAttr: 'photos-tab', placement: 'bottom',
    title: 'Fotos', body: 'Tire fotos direto pelo app — ficam vinculadas à data e etapa da obra.',
  },
  {
    id: 'fvs_foreman', kind: 'tooltip',
    targetAttr: 'fvs-tab', placement: 'bottom',
    title: 'FVS — Ficha de Verificação',
    body: 'Preencha as fichas de qualidade aqui. Cada item tem conformidade + assinatura.',
  },
  {
    id: 'complete_foreman', kind: 'slideout',
    title: '✅ Pronto!',
    body: 'Você já sabe os três principais: diário, etapas e fotos. Housy responde qualquer dúvida.',
    ctaLabel: 'Fechar',
  },
];

export const GENERIC_STEPS: StepDef[] = [
  {
    id: 'welcome_generic', kind: 'slideout',
    title: 'Bem-vindo ao Builder',
    body: 'Explore os projetos disponíveis para você. Clique em qualquer card para começar.',
    ctaLabel: 'Entendido',
  },
  {
    id: 'open_project_generic', kind: 'tooltip', route: '/projects',
    targetAttr: 'project-card', placement: 'bottom',
    title: 'Seus projetos', body: 'Clique para ver o status da obra.',
  },
  {
    id: 'complete_generic', kind: 'slideout',
    title: 'Tudo certo!',
    body: 'Explore à vontade. Housy (IA) está disponível para dúvidas.',
    ctaLabel: 'Fechar',
  },
];

export const FLOW_STEPS: Record<FlowKey, StepDef[]> = {
  pm: PM_STEPS,
  financeiro: FINANCEIRO_STEPS,
  foreman: FOREMAN_STEPS,
  generic: GENERIC_STEPS,
};
